import { useEffect, useRef, useState } from 'react'
import type { PDFDocumentProxy } from '../lib/pdfjs'

interface PdfPageProps {
  pdf: PDFDocumentProxy
  pageNumber: number // 1-based, matching pdfjs's own convention
  width: number
  // Rendered absolutely-positioned over the canvas, once it has a known
  // size — lets callers (SignatureFieldPlacer, SignPage) overlay field
  // boxes/inputs at pixel coordinates derived from the same size.
  children?: (size: { width: number; height: number }) => React.ReactNode
}

export const PdfPage: React.FC<PdfPageProps> = ({ pdf, pageNumber, width, children }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [size, setSize] = useState<{ width: number; height: number } | null>(null)

  useEffect(() => {
    let cancelled = false

    pdf.getPage(pageNumber).then((page) => {
      if (cancelled) return
      const unscaledViewport = page.getViewport({ scale: 1 })
      const viewport = page.getViewport({ scale: width / unscaledViewport.width })
      const canvas = canvasRef.current
      if (!canvas) return

      canvas.width = viewport.width
      canvas.height = viewport.height
      page.render({ canvas, viewport })
      setSize({ width: viewport.width, height: viewport.height })
    })

    return () => {
      cancelled = true
    }
  }, [pdf, pageNumber, width])

  return (
    <div className="relative bg-white shadow-sm border border-gray-200" style={size ? { width: size.width, height: size.height } : { width, height: width * 1.294 }}>
      <canvas ref={canvasRef} className="block" />
      {size && children?.(size)}
    </div>
  )
}
